import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_PRODUCTIONS,
  RECEIVING_PRODUCTIONS,
  RECEIVE_PRODUCTION_METADATA,
  RECEIVE_PRODUCTION_FAILURE,
  SAVING_PRODUCTION,
  SAVING_PRODUCTION_FAILURE,
  UPDATING_PRODUCTION,
  UPDATING_PRODUCTION_FAILURE,
  DELETE_PRODUCTION,
  DELETING_PRODUCTION,
  DELETING_PRODUCTION_FAILURE,
  RESET_PRODUCTION_META,
  CLEAR_PRODUCTION_STATE,
  CLEAR_STATE,
  INCREMENT_PRODUCTION_COUNT,
  SET_ALL_PRODUCTIONS_LOADED
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { getBySearchCriteria } from '@/utils/helpers/GetterHelpers/CommonGetterFunctions'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const productionSchema = new schema.Entity('productions')
const productionListSchema = new schema.Array(productionSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null,
  allProductionsLoaded: false
}

const actions = {
  fetchProductions (store, isNextSet) {
    const urlToFetchFrom = () => {
      if (isNextSet) {
        const nextUrl = store.state.next
        return buildUrl(null, null, nextUrl)
      }
      const instanceUrl = this.getters.getActiveInstance.url
      return buildUrl('productions', null, instanceUrl)
    }
    this.commit(RECEIVING_PRODUCTIONS, true)
    return Vue.http.get(urlToFetchFrom(), { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_PRODUCTIONS, false)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, productionListSchema)
          this.commit(RECEIVE_PRODUCTIONS, { data: entities.productions })
          this.commit(RECEIVE_PRODUCTION_METADATA, { next, previous, count, result: result.length })
          return entities
        }
      })
      .catch(e => {
        this.commit(RECEIVING_PRODUCTIONS, false)
        this.commit(RECEIVE_PRODUCTION_FAILURE, handleXHRError(e))
      })
  },
  fetchProductionsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PRODUCTIONS, true)
    return Vue.http.get(buildUrlWithQuery('productions', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_PRODUCTIONS, false)
        this.commit(RESET_PRODUCTION_META)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, productionListSchema)
          this.commit(RECEIVE_PRODUCTIONS, { data: entities.productions })
          this.commit(RECEIVE_PRODUCTION_METADATA, { next, previous, count, result: result.length })
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVING_PRODUCTIONS, false)
        this.commit(RECEIVE_PRODUCTION_FAILURE, handleXHRError(e))
      })
  },
  setProductionPreloadStatus (store, payload) {
    this.commit(SET_ALL_PRODUCTIONS_LOADED, payload)
  },
  clearProductionState (store, clearCache = false) {
    this.commit(CLEAR_PRODUCTION_STATE, clearCache)
  }
}

const getters = {
  getProductions: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllProductions: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getProductionMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getProductionById: (state, rootState) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getProductionsBySearchCriteria: (state) => (query) => {
    return getBySearchCriteria(state.data, query)
  },
  getProductionByBatchId: (state, rootState) => (batchId) => {
    if (!batchId) return null
    const batch = rootState.getBatchById(batchId)
    if (!(batch && batch.production)) return []
    if (state.data && state.data[batch.production]) {
      return state.data[batch.production]
    } else if (state.cache && state.cache[batch.production]) {
      return state.cache[batch.production]
    } else {
      return []
    }
  },
  getDefaultProduction: (state) => {
    return Object.values(state.cache).find(v => v.active) || {}
  },
  getProductionFetchStatus: (state) => {
    return state.receivingData
  },
  getProductionSaveStatus: (state) => {
    return state.saving
  },
  getProductionUpdateStatus: (state) => {
    return state.updating
  },
  getProductionDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_PRODUCTIONS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_PRODUCTIONS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_PRODUCTION_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_PRODUCTION] (state, payload) {
    state.saving = payload
  },
  [SAVING_PRODUCTION_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_PRODUCTION] (state, payload) {
    state.updating = payload
  },
  [UPDATING_PRODUCTION_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_PRODUCTION] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_PRODUCTION] (state, payload) {
    state.deleting = payload
  },
  [DELETING_PRODUCTION_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_PRODUCTION_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_PRODUCTION_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_PRODUCTION_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_PRODUCTION_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  [SET_ALL_PRODUCTIONS_LOADED] (state, payload) {
    state.allProductionsLoaded = payload
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/production.js